import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { AlertTriangle, TrendingDown, ArrowRight, Loader2, Info } from "lucide-react";
import { formatCurrency } from "../../lib/utils";
import api from "../../lib/api";

/**
 * Dashboard widget — surfaces dishes with squeezed margins so the chef can
 * act (reprice, cut cost, or flag missing data). Computed server-side from
 * dishes + recipes collections (single lightweight query).
 */
export default function CostAlertsCard({ restaurantId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [threshold, setThreshold] = useState(() => {
    try {
      return parseFloat(localStorage.getItem("mg_cost_alert_margin_threshold") || "20") || 20;
    } catch { return 20; }
  });
  const [view, setView] = useState("low_margin"); // "low_margin" | "missing"

  useEffect(() => {
    if (!restaurantId) return;
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        const res = await api.request(
          `/restaurants/${restaurantId}/dashboard/cost-alerts?margin_threshold=${threshold}&limit=10`
        );
        if (!cancel) setData(res);
      } catch {
        if (!cancel) setData(null);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [restaurantId, threshold]);

  const handleThresholdChange = (v) => {
    const n = parseFloat(v) || 20;
    setThreshold(n);
    try { localStorage.setItem("mg_cost_alert_margin_threshold", String(n)); } catch { /* noop */ }
  };

  const counters = useMemo(() => data?.counters || {}, [data?.counters]);
  const activeList = view === "low_margin" ? (data?.low_margin || []) : (data?.missing_signal || []);

  const summary = useMemo(() => {
    if (!counters.analyzable) return null;
    return `${counters.low_margin}/${counters.analyzable} platos bajo ${threshold}% · margen medio ${counters.avg_margin_pct}%`;
  }, [counters, threshold]);

  return (
    <Card className="border-none shadow-soft h-full flex flex-col" data-testid="cost-alerts-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-amber-700" />
            Platos con margen estrangulado
          </CardTitle>
          <div className="flex items-center gap-1 text-xs">
            <span className="text-muted-foreground">Umbral</span>
            <select
              value={threshold}
              onChange={(e) => handleThresholdChange(e.target.value)}
              className="h-6 text-xs rounded-md border border-border bg-background px-1.5"
              data-testid="cost-alerts-threshold"
              aria-label="Umbral de margen para alertas de costes"
            >
              <option value={15}>15%</option>
              <option value={20}>20%</option>
              <option value={30}>30%</option>
              <option value={40}>40%</option>
            </select>
          </div>
        </div>
        {summary && (
          <p className="text-xs text-muted-foreground mt-1">{summary}</p>
        )}
      </CardHeader>

      <CardContent className="flex-1 overflow-hidden flex flex-col gap-2 pt-0">
        {/* Tab toggle */}
        <div className="flex items-center gap-1 text-[11px]">
          <button
            type="button"
            onClick={() => setView("low_margin")}
            className={`px-2 py-0.5 rounded-full border transition ${
              view === "low_margin"
                ? "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800 font-medium"
                : "bg-transparent text-muted-foreground border-border/40 hover:bg-muted"
            }`}
            data-testid="cost-alerts-tab-low-margin"
          >
            Margen bajo ({counters.low_margin ?? 0})
          </button>
          <button
            type="button"
            onClick={() => setView("missing")}
            className={`px-2 py-0.5 rounded-full border transition ${
              view === "missing"
                ? "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800 font-medium"
                : "bg-transparent text-muted-foreground border-border/40 hover:bg-muted"
            }`}
            data-testid="cost-alerts-tab-missing"
          >
            Sin datos ({counters.missing_signal ?? 0})
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : activeList.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-6 gap-2 text-muted-foreground">
            <div className="w-9 h-9 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-emerald-700" />
            </div>
            <p className="text-xs">
              {view === "low_margin"
                ? `Ningún plato por debajo del ${threshold}% de margen. 👏`
                : "Todos los platos tienen precio y coste informados."}
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
            {activeList.map((d) => (
              <button
                key={d.dish_id}
                onClick={() => navigate(`/app/recipes?dish=${d.dish_id}`)}
                className="w-full text-left flex items-center gap-2 p-2 rounded-lg border border-border/40 bg-muted/30 hover:bg-muted transition-colors"
                data-testid={`cost-alert-row-${d.dish_id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{d.name}</p>
                  {view === "low_margin" ? (
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                      <span>{formatCurrency(d.price)} PVP</span>
                      <span>·</span>
                      <span>{formatCurrency(d.cost_per_serving)} coste</span>
                    </p>
                  ) : (
                    <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                      <Info className="w-3 h-3" />
                      {d.reason === "missing_price" ? "Falta PVP" : "Falta coste"}
                    </p>
                  )}
                </div>
                {view === "low_margin" ? (
                  <Badge
                    variant="outline"
                    className={`text-[10px] font-semibold ${
                      d.margin_pct < 10
                        ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900"
                        : d.margin_pct < threshold
                        ? "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900"
                        : "bg-emerald-50 text-emerald-700 border-emerald-200"
                    }`}
                  >
                    {d.margin_pct}% margen
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px]">
                    {d.reason === "missing_price" ? "Sin PVP" : "Sin coste"}
                  </Badge>
                )}
                <ArrowRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              </button>
            ))}
          </div>
        )}

        <div className="pt-1 border-t border-border/40">
          <Button
            variant="ghost" size="sm"
            className="w-full text-xs h-8"
            onClick={() => navigate("/app/recipes")}
            data-testid="cost-alerts-view-all"
          >
            Revisar todas las recetas
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
